import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Camera, Search, Map as MapIcon, Users, Database, Settings, Shield, Eye, Clock, Bell, FileText, Download, LogOut, Key } from 'lucide-react';
import VehicleTracking from '@/components/VehicleTracking';
import { VehicleTrackingBoundary } from '@/components/VehicleTrackingBoundary';
import LiveCameras from '@/components/LiveCameras';
import InteractiveMap from '@/components/InteractiveMap';
import InvestigationHistory from '@/components/InvestigationHistory';
import AccountSettings from '@/components/AccountSettings';
import PlansModal from "./PlansModal";
import PausedAccountModal from "./PausedAccountModal";
import CheckoutTransparente from "./CheckoutTransparente";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { authFetch, API_BASE_URL } from "../lib/authFetch";
import { toast } from "sonner";
import { useClientSession } from "@/hooks/useClientSession";

import AlertsPanel from '@/components/AlertsPanel';
import FaceRecognition from '@/components/FaceRecognition';
import { History } from 'lucide-react';

// Dias após o vencimento (sem saldo no cartão) para exibir o modal de reativar assinatura
const DIAS_PARA_MODAL_REATIVAR = 7;

const Dashboard = () => {
  const navigate = useNavigate();
  
  // Hook para gerenciar sessão automática (10min inatividade / 1 dia absoluto)
  useClientSession();
  
  const [activeTab, setActiveTab] = useState("overview");
  const [userName, setUserName] = useState("Cliente");
  const [userData, setUserData] = useState(null);
  const [novoNome, setNovoNome] = useState("");
  const [userPlan, setUserPlan] = useState("Profissional");
  const [subscription, setSubscription] = useState(null);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [indicators, setIndicators] = useState({
    consultas_hoje: 0,
    consultas_hoje_change: 0,
    alertas_ativos: 0,
    alertas_ativos_change: 0,
    investigacoes: 0,
    investigacoes_change: 0,
    relatorios: 0,
    relatorios_change: 0
  });
  const [loadingIndicators, setLoadingIndicators] = useState(true);
  const [quickSearchPlate, setQuickSearchPlate] = useState("");
  const [quickSearchRequestId, setQuickSearchRequestId] = useState(0);
  const [recentAlerts, setRecentAlerts] = useState<any[]>([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [showPausedModal, setShowPausedModal] = useState(false);
  const [showPaymentRequired, setShowPaymentRequired] = useState(false);
  const [planoParaPagar, setPlanoParaPagar] = useState<any>(null);
  const [checkingPayment, setCheckingPayment] = useState(true);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  
  // Estados para troca de senha obrigatória (clientes criados pelo admin)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Estados para trial expirado
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

  // Função de logout manual
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('userData');
    localStorage.removeItem('userPlan');
    localStorage.removeItem('session_start');
    localStorage.removeItem('must_change_password');
    localStorage.removeItem('access_type');
    localStorage.removeItem('trial_expired');
    localStorage.removeItem('trial_end_date');
    navigate('/');
    toast.success('Você foi desconectado com sucesso.');
  };

  // Verificar se precisa trocar senha ou se trial expirou
  useEffect(() => {
    const mustChangePassword = localStorage.getItem('must_change_password') === 'true';
    const trialExpired = localStorage.getItem('trial_expired') === 'true';
    const accessType = localStorage.getItem('access_type');
    
    if (mustChangePassword) {
      setShowChangePasswordModal(true);
    } else if (trialExpired && accessType?.startsWith('trial')) {
      setShowTrialExpiredModal(true);
    }
  }, []);

  // Função para trocar a senha obrigatória
  const handleChangePassword = async () => {
    if (!newPassword.trim() || newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await authFetch('/change-password-required', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword })
      });

      if (response.ok) {
        toast.success('Senha alterada com sucesso!');
        localStorage.removeItem('must_change_password');
        setShowChangePasswordModal(false);
        setNewPassword('');
        setConfirmPassword('');
        
        // Verificar se também tem trial expirado (só para trial_7d, não para free)
        const trialExpired = localStorage.getItem('trial_expired') === 'true';
        const accessType = localStorage.getItem('access_type');
        // Não mostrar modal de planos para usuários com acesso liberado (free)
        if (trialExpired && accessType?.startsWith('trial')) {
          setShowTrialExpiredModal(true);
        }
        // Se for free, não precisa de nada adicional
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao alterar senha');
      }
    } catch (e) {
      toast.error('Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  // Buscar dados do usuário e garantir que indicadores/atividades são sempre do usuário atual
  useEffect(() => {
    const fetchUserData = async () => {
      const response = await authFetch("/users/me");
      if (response.ok) {
        const data = await response.json();
        setUserData(data);
        setUserName(data.nome_completo || "Cliente");
        setQuickSearchPlate(''); // Evita mostrar placa/termo de outra sessão
        if (data.access_type) localStorage.setItem("access_type", data.access_type);
        if (data.trial_end_date) localStorage.setItem("trial_end_date", data.trial_end_date);
        // Resetar indicadores e atividades para não exibir dados de sessão anterior
        setIndicators({
          consultas_hoje: 0,
          consultas_hoje_change: 0,
          alertas_ativos: 0,
          alertas_ativos_change: 0,
          investigacoes: 0,
          investigacoes_change: 0,
          relatorios: 0,
          relatorios_change: 0
        });
        setRecentActivities([]);
        setLoadingIndicators(true);
        setLoadingActivities(true);
        // Recarregar indicadores e atividades do usuário atual
        try {
          const [indRes, actRes] = await Promise.all([
            authFetch("/dashboard/indicators"),
            authFetch("/atividades/recentes")
          ]);
          if (indRes.ok) {
            const indData = await indRes.json();
            setIndicators(indData);
          }
          if (actRes.ok) {
            const actData = await actRes.json();
            setRecentActivities(actData);
          } else {
            setRecentActivities([]);
          }
        } catch (_) {
          setRecentActivities([]);
        } finally {
          setLoadingIndicators(false);
          setLoadingActivities(false);
        }
      } else if (response.status === 403) {
        // Conta pausada
        setShowPausedModal(true);
      } else {
        // Token inválido ou expirado
        localStorage.removeItem("token");
        navigate("/");
      }
    };

    fetchUserData();
  }, [navigate]);

  useEffect(() => {
    const plan = localStorage.getItem("userPlan");
    if (plan) setUserPlan(plan);
  }, []);

  useEffect(() => {
    const fetchSubscription = async () => {
      setCheckingPayment(true);
      try {
        const response = await authFetch("/subscriptions/me");
        
        // Se não tem assinatura (404), verificar se é acesso liberado pelo admin
        if (response.status === 404) {
          const accessType = localStorage.getItem("access_type");
          const trialEndStr = localStorage.getItem("trial_end_date");
          const isFree = accessType === "free";
          const isTrialActive = accessType?.startsWith("trial") && trialEndStr && new Date(trialEndStr) >= new Date();
          if (isFree || isTrialActive) {
            // Cliente criado pelo admin com acesso liberado ou trial ativo: não exige plano
            setShowPlanSelection(false);
            setCheckingPayment(false);
            return;
          }
          setShowPlanSelection(true);
          setCheckingPayment(false);
          return;
        }
        
        if (response.ok) {
          const data = await response.json();
          setSubscription(data);
          
          // Modal "Reativar assinatura": quando o pagamento falhou (ex.: sem saldo) e já passaram X dias do vencimento
          if (data.next_payment_date && data.auto_renew) {
            const vencimento = new Date(data.next_payment_date);
            const hoje = new Date();
            hoje.setHours(0, 0, 0, 0);
            vencimento.setHours(0, 0, 0, 0);
            if (vencimento < hoje) {
              const diasAtraso = Math.floor((hoje.getTime() - vencimento.getTime()) / (1000 * 60 * 60 * 24));
              if (diasAtraso >= DIAS_PARA_MODAL_REATIVAR) {
                setShowReactivateModal(true);
              }
            }
          }
          
          // Verificar se o pagamento foi feito
          // Status que indicam que PAGOU: ativo, ativa, active, authorized, approved
          const statusPago = ['ativo', 'ativa', 'active', 'authorized', 'approved'];
          const pagou = statusPago.includes(data.status);
          
          if (!pagou && data.plan) {
            // Buscar dados do plano para mostrar checkout
            const planosRes = await fetch(`${API_BASE_URL}/planos`);
            const planosData = await planosRes.json();
            const planoAtual = (planosData.planos || []).find((p: any) => p.nome === data.plan);
            
            if (planoAtual) {
              setPlanoParaPagar({
                id: planoAtual.slug,
                nome: planoAtual.nome,
                valor: planoAtual.valor,
                descricao: planoAtual.descricao || planoAtual.nome
              });
              setShowPaymentRequired(true);
            } else {
              // Plano não encontrado, mostra modal para escolher
              setShowPlanSelection(true);
            }
          }
        }
      } catch {
        // Erro ao verificar assinatura
      } finally {
        setCheckingPayment(false);
      }
    };
    fetchSubscription();
  }, []);

  // Se o usuário tem acesso liberado (free) ou trial ativo, não mostrar modal de planos
  useEffect(() => {
    if (!userData || !showPlanSelection) return;
    const at = userData.access_type;
    const trialEnd = userData.trial_end_date;
    const isFree = at === "free";
    const isTrialActive = at?.startsWith?.("trial") && trialEnd && new Date(trialEnd) >= new Date();
    if (isFree || isTrialActive) setShowPlanSelection(false);
  }, [userData, showPlanSelection]);

  // Recarrega indicadores quando voltar para a aba overview
  useEffect(() => {
    const fetchIndicators = async () => {
      setLoadingIndicators(true);
      const response = await authFetch("/dashboard/indicators");
      if (response.ok) {
        const data = await response.json();
        setIndicators(data);
      }
      setLoadingIndicators(false);
    };
    if (activeTab === "overview") {
      fetchIndicators();
    }
  }, [activeTab]);

  useEffect(() => {
    const fetchRecentAlerts = async () => {
      try {
        const response = await authFetch("/alertas/recentes");
        if (response.ok) {
          const data = await response.json();
          setRecentAlerts(data);
        } else {
          setRecentAlerts([]);
        }
      } catch {
        setRecentAlerts([]);
      } finally {
        setLoadingAlerts(false);
      }
    };
    fetchRecentAlerts();
  }, []);

  useEffect(() => {
    const fetchRecentActivities = async () => {
      const response = await authFetch("/atividades/recentes");
      if (response.ok) {
        const data = await response.json();
        setRecentActivities(data);
      } else {
        setRecentActivities([]);
      }
      setLoadingActivities(false);
    };
    fetchRecentActivities();

    // Listener para evento global de atividade registrada
    const handleAtividadeRegistrada = () => {
      setLoadingActivities(true);
      fetchRecentActivities();
    };
    window.addEventListener('atividade-registrada', handleAtividadeRegistrada);
    return () => {
      window.removeEventListener('atividade-registrada', handleAtividadeRegistrada);
    };
  }, []);

  const stats = [
    {
      title: "Consultas Hoje",
      value: loadingIndicators ? "..." : indicators.consultas_hoje,
      icon: <Search className="w-5 h-5" />,
      color: "text-protec-gold"
    },
    {
      title: "Alertas Ativos",
      value: loadingIndicators ? "..." : indicators.alertas_ativos,
      icon: <Bell className="w-5 h-5" />,
      color: "text-protec-gold"
    },
    {
      title: "Investigações",
      value: loadingIndicators ? "..." : indicators.investigacoes,
      icon: <Shield className="w-5 h-5" />,
      color: "text-protec-gold"
    },
    {
      title: "Relatórios",
      value: loadingIndicators ? "..." : indicators.relatorios,
      icon: <FileText className="w-5 h-5" />,
      color: "text-protec-gold"
    }
  ];

  // Função para busca rápida de veículo
  const handleQuickVehicleSearch = (plate: string) => {
    const normalized = plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 7);
    if (!normalized) return;
    setQuickSearchPlate(normalized);
    setQuickSearchRequestId((v) => v + 1);
    setActiveTab("vehicles");
  };

  // Funções para pagamento
  const handlePaymentSuccess = async (data: any) => {
    setShowPaymentRequired(false);
    setPlanoParaPagar(null);
    toast.success("Pagamento realizado com sucesso! Bem-vindo ao ProtecBens!");
    
    // Recarrega a assinatura
    const response = await authFetch("/subscriptions/me");
    if (response.ok) {
      const subData = await response.json();
      setSubscription(subData);
    }
  };

  const handlePaymentCancel = () => {
    // Usuário cancelou - volta para seleção de planos
    setShowPaymentRequired(false);
    setPlanoParaPagar(null);
    setShowPlanSelection(true);
  };

  // Função quando usuário escolhe um plano no modal
  const handlePlanSelected = async (planName: string) => {
    setShowPlanSelection(false);
    
    // Buscar dados do plano escolhido
    try {
      const planosRes = await fetch(`${API_BASE_URL}/planos`);
      const planosData = await planosRes.json();
      const planoEscolhido = (planosData.planos || []).find((p: any) => p.nome === planName);
      
      if (planoEscolhido) {
        setPlanoParaPagar({
          id: planoEscolhido.slug,
          nome: planoEscolhido.nome,
          valor: planoEscolhido.valor,
          descricao: planoEscolhido.descricao || planoEscolhido.nome
        });
        setShowPaymentRequired(true);
      }
    } catch (error) {
      toast.error("Erro ao carregar plano");
    }
  };

  // Mostrar loading enquanto verifica pagamento
  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-protec-dark flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-protec-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Verificando sua conta...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Modal de seleção de plano */}
      <PlansModal 
        open={showPlanSelection}
        subscription={null}
        onClose={() => {}} // Não pode fechar sem escolher
        onPlanChanged={handlePlanSelected}
      />

      {/* Modal de pagamento */}
      {showPaymentRequired && planoParaPagar && (
        <CheckoutTransparente
          plano={planoParaPagar}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
        />
      )}

      {/* Modal: Reativar assinatura (pagamento falhou há X+ dias, ex.: sem saldo no cartão) */}
      <Dialog open={showReactivateModal} onOpenChange={setShowReactivateModal}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Reativar assinatura
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-300 py-2">
            O pagamento da sua assinatura não pôde ser processado (por exemplo: saldo insuficiente no cartão).
            Após <strong>{DIAS_PARA_MODAL_REATIVAR} dias</strong> do vencimento, é necessário atualizar a forma de pagamento para reativar.
          </p>
          <DialogFooter>
            <Button variant="outline" className="border-gray-500 text-gray-400" onClick={() => setShowReactivateModal(false)}>
              Fechar
            </Button>
            <Button
              className="bg-protec-gold text-black hover:bg-protec-gold-light"
              onClick={async () => {
                setShowReactivateModal(false);
                if (!subscription?.plan) return;
                try {
                  const planosRes = await fetch(`${API_BASE_URL}/planos`);
                  const planosData = await planosRes.json();
                  const planoAtual = (planosData.planos || []).find((p: any) => p.nome === subscription.plan);
                  if (planoAtual) {
                    setPlanoParaPagar({
                      id: planoAtual.slug,
                      nome: planoAtual.nome,
                      valor: planoAtual.valor,
                      descricao: planoAtual.descricao || planoAtual.nome
                    });
                    setShowPaymentRequired(true);
                  } else {
                    toast.error("Plano não encontrado. Escolha um plano.");
                    setShowPlanSelection(true);
                  }
                } catch {
                  toast.error("Erro ao carregar plano");
                }
              }}
            >
              Reativar assinatura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    <div className="min-h-screen bg-gradient-to-br from-protec-dark via-protec-dark-light to-protec-dark-lighter text-white">
      {/* Header */}
      <header className="border-b border-protec-gold/20 bg-black/20 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <img 
              src="/logo.png" 
              alt="Protec Bens" 
              className="h-8 w-8 sm:h-10 sm:w-10"
            />
            <div>
              <h1 className="text-base sm:text-xl font-bold gradient-text truncate max-w-[120px] sm:max-w-none">{userName.toUpperCase()}</h1>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Badge className="bg-protec-gold/20 text-protec-gold border-protec-gold/30 text-xs sm:text-sm hidden xs:flex">
              <Eye className="w-3 h-3 mr-1" />
              <span className="hidden sm:inline">Plano </span>{subscription?.plan || "Profissional"}
            </Badge>
            <Button 
              variant="outline" 
              size="sm"
              className="border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white h-8 w-8 sm:h-9 sm:w-9 p-0"
              onClick={() => setActiveTab('settings')}
              title="Configurações"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              className="border-gray-700 text-gray-500 hover:bg-gray-700 hover:text-gray-300 h-8 w-8 sm:h-9 sm:w-9 p-0"
              onClick={() => setShowLogoutConfirm(true)}
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 sm:grid-cols-7 bg-protec-dark-light mb-4 sm:mb-6 h-auto gap-1 p-1">
            <TabsTrigger 
              value="overview" 
              className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3"
            >
              <Database className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Painel</span>
            </TabsTrigger>
            <TabsTrigger 
              value="vehicles" 
              className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3"
            >
              <Search className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Veículos</span>
            </TabsTrigger>
            <TabsTrigger value="faces" className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Pessoas</span>
            </TabsTrigger>
            <TabsTrigger 
              value="cameras" 
              className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3"
            >
              <Camera className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Câmeras</span>
            </TabsTrigger>
            <TabsTrigger 
              value="map" 
              className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3"
            >
              <MapIcon className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Mapa</span>
            </TabsTrigger>
            <TabsTrigger 
              value="history" 
              className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3"
            >
              <History className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Histórico</span>
            </TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-protec-gold data-[state=active]:text-black text-xs sm:text-sm py-2 px-1 sm:px-3">
              <Bell className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Alertas</span>
            </TabsTrigger>
            {/* Relatórios — versão futura
            <TabsTrigger value="reports" className="data-[state=active]:bg-protec-gold data-[state=active]:text-black">
              <FileText className="w-4 h-4 mr-2" />
              Relatórios
            </TabsTrigger>
            */}
          </TabsList>

          <TabsContent value="overview">
            <div className="space-y-4 sm:space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                {stats.map((stat, index) => (
                  <Card key={index} className="bg-protec-dark-light border-protec-gold/20">
                    <CardContent className="p-3 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs sm:text-sm text-gray-400">{stat.title}</p>
                          <p className="text-lg sm:text-2xl font-bold text-white">{stat.value}</p>
                        </div>
                        <div className={`${stat.color}`}>
                          {stat.icon}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <Card className="bg-protec-dark-light border-protec-gold/20">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                      <Search className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
                      Busca Rápida de Veículo
                    </CardTitle>
                    <CardDescription className="text-gray-300 text-xs sm:text-sm">
                      Digite a placa para rastreamento imediato
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input 
                        placeholder="ABC-1234 ou ABC1234"
                        className="bg-protec-dark border-protec-gold/30 text-white"
                        value={quickSearchPlate}
                        onChange={e => setQuickSearchPlate(e.target.value.toUpperCase())}
                        maxLength={8}
                        autoComplete="off"
                      />
                      <Button className="bg-protec-gold text-black hover:shadow-gold w-full sm:w-auto"
                        onClick={() => handleQuickVehicleSearch(quickSearchPlate)}
                        disabled={!quickSearchPlate.trim()}
                      >
                        Buscar
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-protec-dark-light border-protec-gold/20">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                      <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
                      Alertas Recentes
                    </CardTitle>
                    <CardDescription className="text-gray-300 text-xs sm:text-sm">
                      Últimas detecções e notificações
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {loadingAlerts ? (
                        <div className="text-gray-400 text-sm">Carregando...</div>
                      ) : recentAlerts.length === 0 ? (
                        <div className="text-gray-400 text-sm">Nenhum alerta</div>
                      ) : (
                        recentAlerts.slice(0, 3).map((alert, idx) => (
                          <div key={alert.id || idx} className="flex items-center gap-2 text-xs sm:text-sm">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${alert.status === 'ativo' ? 'bg-red-400' : 'bg-green-400'}`}></div>
                            <span className="text-gray-300 truncate">
                              {alert.tipo === 'placa' ? (
                                <>Veículo {alert.detecao?.placa || alert.descricao?.match(/([A-Z0-9]{6,8})/)?.[1] || '---'} detectado - {new Date(alert.data_hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</>
                              ) : alert.descricao ? (
                                <>{alert.descricao} - {new Date(alert.data_hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</>
                              ) : alert.tipo ? (
                                <>{alert.tipo} - {new Date(alert.data_hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</>
                              ) : (
                                <>Alerta - {new Date(alert.data_hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</>
                              )}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Grid com Atividade Recente e SAC */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Recent Activity */}
                <Card className="bg-protec-dark-light border-protec-gold/20">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
                      Atividade Recente
                    </CardTitle>
                    <CardDescription className="text-gray-300 text-xs sm:text-sm">
                      Suas últimas consultas e ações na plataforma
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 sm:space-y-4">
                      {loadingActivities ? (
                        <div className="text-gray-400 text-sm">Carregando...</div>
                      ) : recentActivities.length === 0 ? (
                        <div className="text-gray-400 text-sm">Nenhuma atividade recente.</div>
                      ) : (
                        recentActivities.slice(0, 5).map((activity, index) => (
                          <div key={index} className="flex items-center justify-between py-2 border-b border-protec-gold/10 gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-white text-xs sm:text-sm font-medium truncate">{activity.tipo}</p>
                              <p className="text-gray-400 text-xs">
                                Placa: <span className="text-protec-gold font-semibold">{activity.target}</span>
                              </p>
                            </div>
                            <div className="text-xs text-protec-gold text-right flex-shrink-0">
                              {activity.data_hora && activity.data_hora !== '-' ? (
                                <>
                                  <div>{new Date(activity.data_hora).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                                  <div>{new Date(activity.data_hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                                </>
                              ) : '--/--/---- --:--'}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* SAC - Atendimento ao Cliente */}
                <Card className="bg-protec-dark-light border-protec-gold/20">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
                      SAC - Atendimento ao Cliente
                    </CardTitle>
                    <CardDescription className="text-gray-300 text-xs sm:text-sm">
                      Entre em contato conosco para suporte
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* WhatsApp / Telefone */}
                      <a 
                        href="https://wa.me/5547997428587" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-protec-dark rounded-lg border border-protec-gold/10 hover:border-protec-gold/30 transition-colors group"
                      >
                        <div className="w-10 h-10 rounded-full bg-green-600/20 flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium group-hover:text-protec-gold transition-colors">WhatsApp</p>
                          <p className="text-gray-400 text-xs">(47) 99742-8587</p>
                        </div>
                        <svg className="w-4 h-4 text-gray-500 group-hover:text-protec-gold transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>

                      {/* E-mail */}
                      <a 
                        href="mailto:protecbensanalitico@gmail.com" 
                        className="flex items-center gap-3 p-3 bg-protec-dark rounded-lg border border-protec-gold/10 hover:border-protec-gold/30 transition-colors group"
                      >
                        <div className="w-10 h-10 rounded-full bg-protec-gold/20 flex items-center justify-center">
                          <svg className="w-5 h-5 text-protec-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium group-hover:text-protec-gold transition-colors">E-mail</p>
                          <p className="text-gray-400 text-xs break-all">protecbensanalitico@gmail.com</p>
                        </div>
                        <svg className="w-4 h-4 text-gray-500 group-hover:text-protec-gold transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>

                      <p className="text-gray-500 text-xs text-center pt-2">
                        Horário de atendimento: Seg a Sex, 8h às 18h
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Assinatura e Cobrança e botão de trocar de plano removidos do painel principal */}
              <PlansModal
                open={showPlansModal}
                subscription={subscription}
                onClose={() => setShowPlansModal(false)}
                onPlanChanged={(newSub) => setSubscription(newSub)}
              />
            </div>
          </TabsContent>

          <TabsContent value="vehicles">
            <VehicleTrackingBoundary fallbackMessage="Não foi possível exibir o rastreamento de placas. Toque em Tentar novamente.">
              <VehicleTracking
                key={`vehicles-${quickSearchRequestId}`}
                initialPlate={quickSearchPlate}
                searchRequestId={quickSearchRequestId}
              />
            </VehicleTrackingBoundary>
          </TabsContent>

          <TabsContent value="cameras">
            <LiveCameras />
          </TabsContent>

          <TabsContent value="map">
            <InteractiveMap />
          </TabsContent>

          <TabsContent value="history">
            <VehicleTrackingBoundary fallbackMessage="Não foi possível exibir o histórico. Recarregue a página.">
              <InvestigationHistory />
            </VehicleTrackingBoundary>
          </TabsContent>

          <TabsContent value="alerts">
            <AlertsPanel />
          </TabsContent>

          {/* Tab de configurações - acessível pelo botão engrenagem no header */}
          <TabsContent value="settings">
            <AccountSettings userData={userData} setUserName={setUserName} onSubscriptionChange={setSubscription} />
          </TabsContent>

          <TabsContent value="faces">
            <FaceRecognition />
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de Conta Pausada */}
      <PausedAccountModal 
        open={showPausedModal} 
        onOpenChange={setShowPausedModal} 
      />

      {/* Modal de Confirmação de Logout */}
      <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Confirmar Saída</DialogTitle>
          </DialogHeader>
          <p className="text-gray-300 py-4">
            Tem certeza que deseja sair da sua conta?
          </p>
          <DialogFooter>
            <Button 
              onClick={handleLogout}
              className="bg-protec-gold text-black hover:bg-protec-gold-light"
            >
              Sair
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Troca de Senha Obrigatória - Discreto */}
      <Dialog open={showChangePasswordModal} onOpenChange={() => {}}>
        <DialogContent className="bg-protec-dark border-protec-gold/10 text-white max-w-sm [&>button]:hidden">
          <div className="space-y-4">
            <p className="text-gray-400 text-xs text-center">
              Crie uma nova senha para continuar
            </p>

            <div className="space-y-3">
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nova senha"
                className="bg-protec-dark-light border-protec-gold/20 text-white text-sm h-10"
              />

              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmar senha"
                className="bg-protec-dark-light border-protec-gold/20 text-white text-sm h-10"
              />
            </div>

            <Button 
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="w-full bg-protec-gold/90 text-black hover:bg-protec-gold text-sm h-9"
            >
              {changingPassword ? 'Salvando...' : 'Continuar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Trial Expirado */}
      <Dialog open={showTrialExpiredModal} onOpenChange={() => {}}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white max-w-md [&>button]:hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Período de Teste Encerrado
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
              <p className="text-amber-400 text-sm">
                Seu período de teste de <strong>7 dias</strong> chegou ao fim. 
                Para continuar utilizando a plataforma, escolha um plano de assinatura.
              </p>
            </div>

            <p className="text-gray-300 text-sm mb-4">
              Não se preocupe! Todos os seus dados foram preservados e estarão disponíveis após a ativação do seu plano.
            </p>
          </div>

          <DialogFooter className="flex flex-col gap-2">
            <Button 
              onClick={() => {
                setShowTrialExpiredModal(false);
                localStorage.removeItem('trial_expired');
                setShowPlansModal(true);
              }}
              className="w-full bg-protec-gold text-black hover:bg-protec-gold-light"
            >
              Escolher um Plano
            </Button>
            <Button 
              variant="outline"
              onClick={handleLogout}
              className="w-full border-gray-500 text-gray-400 hover:bg-gray-700"
            >
              Sair da Conta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
};

export default Dashboard;
